import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Send } from "lucide-react";
import { api } from "@/lib/api";
import VentCard from "@/components/VentCard";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { timeAgo } from "@/lib/constants";
import { toast } from "sonner";

export default function PostDetail({ user }) {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    const [p, c] = await Promise.all([
      api.get(`/posts/${id}`, { params: { user_id: user.id } }),
      api.get(`/posts/${id}/comments`),
    ]);
    setPost(p.data);
    setComments(c.data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  const submit = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/posts/${id}/comments`, {
        user_id: user.id,
        content: text,
      });
      setComments((prev) => [...prev, data]);
      setText("");
      toast.success("You've been heard.");
    } finally {
      setSending(false);
    }
  };

  if (!post) return <div className="py-16 text-center" style={{ color: "var(--exhale-text-2)" }}>Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <Link to="/" className="inline-flex items-center gap-1 text-sm" style={{ color: "var(--exhale-text-2)" }} data-testid="back-to-feed">
        <ArrowLeft className="w-4 h-4" /> Back to feed
      </Link>

      <VentCard post={post} user={user} onUpdate={setPost} linkable={false} />

      <section
        className="rounded-2xl border p-5 sm:p-6"
        style={{ background: "var(--exhale-surface)", borderColor: "var(--exhale-border)" }}
      >
        <h3 className="font-display text-lg font-semibold mb-4" style={{ color: "var(--exhale-text)" }}>
          Anonymous replies
        </h3>

        <div className="space-y-4" data-testid="comments-list">
          {comments.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--exhale-text-muted)" }}>Be the first to send some empathy.</p>
          ) : (
            comments.map((c) => (
              <div key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center font-mono text-xs font-bold shrink-0"
                  style={{ background: "var(--exhale-badge)", color: "var(--exhale-calm)", border: "1px solid var(--exhale-border)" }}
                >
                  {c.handle.slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs mb-1" style={{ color: "var(--exhale-text-muted)" }}>
                    <span className="font-semibold" style={{ color: "var(--exhale-text-2)" }}>{c.handle}</span>
                    <span className="mx-1.5">·</span>
                    <span className="font-mono">{timeAgo(c.created_at)}</span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap" style={{ color: "var(--exhale-text)" }}>{c.content}</p>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="mt-6">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Reply anonymously as yourself…"
            rows={3}
            data-testid="comment-textarea"
            className="resize-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            style={{ background: "var(--exhale-bg)", color: "var(--exhale-text)" }}
          />
          <div className="flex justify-end mt-3">
            <Button
              onClick={submit}
              disabled={sending || !text.trim()}
              data-testid="submit-comment-btn"
              className="rounded-full font-semibold"
              style={{ background: "var(--exhale-accent)", color: "#0C0B0E" }}
            >
              <Send className="w-4 h-4 mr-2" />
              {sending ? "Sending…" : "Send reply"}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
